import React, { useState, useEffect, useMemo } from "react";
import Autocomplete from "@mui/material/Autocomplete";
import {
  Box, Button, Typography, Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, TableSortLabel, useTheme, Checkbox, IconButton, TextField, InputAdornment, MenuItem,
  Select, Chip, Avatar, Dialog, DialogTitle, DialogContent, Card, Tooltip,
  CardContent,
  CardActions, DialogActions, Divider
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { Helmet } from "react-helmet";

import CloseIcon from "@mui/icons-material/Close";

import PeopleIcon from '@mui/icons-material/People';

import PendingActionsIcon from "@mui/icons-material/PendingActions";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import WarningIcon from '@mui/icons-material/Warning';
import PageLoader from "../components/PageLoader";
import SectionLoader from "../components/SectionLoader";
import Toast from '../components/Toast';
import SettingsIcon from '@mui/icons-material/Settings';
import ModuleSettingsModal from "../components/ModuleSettingsModal";

import { useUser } from "../context/UserContext";
const statusColors = {
  Approve: "success",
  Pending: "warning",
  Decline: "default"
};

function StatCard({ label, count, IconComponent }) {
  return (
    <Card variant="outlined" sx={{ height: "100%", minWidth: { xs: '100%', sm: 200 }, width: "100%", borderRadius: 2 }}>
      <CardContent sx={{ minHeight: 80 }}>
        <Box display="flex" alignItems="center" sx={{ width: "100%" }}>
          <Box
            sx={{
              height: 48,
              width: 48,
              backgroundColor: 'grey.200',
              borderRadius: 1, // for rounded square, use '50%' for circle
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mr: 2,
              flexShrink: 0,
            }}
          >
            <IconComponent sx={{ fontSize: 28, color: '#177373' }} />
          </Box>

          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="h5" fontWeight="bold" noWrap>
              {count}
            </Typography>
            <Typography variant="body2" color="text.secondary" noWrap>
              {label}
            </Typography>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
}
function formatDate(dateStr) {
  const date = new Date(dateStr);
  return date.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

const Drivers = ({ hideHeader = false }) => {
  const muiTheme = useTheme();
  const isLight = muiTheme.palette.mode === 'light';
  const [sortOrder, setSortOrder] = useState('asc');
  const [toast, setToast] = useState({ open: false, message: '', severity: 'success' });
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("All Status");
  const [drivers, setDrivers] = useState([]);
  const [organizations, setOrganizations] = useState([]);
  const [organization, setOrganization] = useState(null); // or orgOptions[0] for default
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const { user } = useUser();
  const [AllEvents, SetAllEvents] = useState(0);
  const [UpcomingEvents, SetUpcomingEvents] = useState(0);
  const [ThisMonthEvents, SetThisMonthEvents] = useState(0);
  const [CompletedEvents, SetCompletedEvents] = useState(0);

  const [origanisation, SetOriganisation] = useState('');

  const [openImage, setOpenImage] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [selectedDriverForView, setSelectedDriverForView] = useState(null);
  const [driverDetailModalOpen, setDriverDetailModalOpen] = useState(false);

  const [selected, setSelected] = useState([]);
  const [loading, setLoading] = useState(true);
  const [settingsOpen, setSettingsOpen] = useState(false);

  const API_URL = process.env.REACT_APP_API_URL;

  const orgOptions = [{ id: 0, org_name: "All Organizations" }, ...organizations];

  const handleSelectAll = (event) => {
    if (event.target.checked) {
      setSelected(drivers.map((driver) => driver.id));
    } else {
      setSelected([]);
    }
  };


  useEffect(() => {
    const fetchUsers = async () => {
      try {
        setLoading(true);
        const token = localStorage.getItem('token');
        const response = await fetch(`${API_URL}driver-list`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            search: search,
            status: status,
            organization: organization?.id || null,
            page: page,
            pageSize: pageSize
          })
        });
        const data = await response.json();

        if (!response.ok || data.success === false) {
          // 🔑 Handle invalid token
          if (data.message === 'Invalid token') {
            localStorage.removeItem('token');
            window.location.href = '/login'; // redirect to login
            return;
          }


        }


        setDrivers(data.data);
        setTotalItems(data.total);
        setTotalPages(data.pagination, totalPages);

      } catch (err) {
        // setError('Network error. Please try again.');
      } finally {
        setLoading(false);
      }
    };
    fetchUsers();
  }, [status, organization, search, page, pageSize]);

  useEffect(() => {
    const fetchRolesAndOrganizations = async () => {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}roles-organizations`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      const data = await response.json();

      if (!response.ok || data.success === false) {
        // 🔑 Handle invalid token
        if (data.message === 'Invalid token') {
          localStorage.removeItem('token');
          window.location.href = '/login'; // redirect to login
          return;
        }


      }
      setOrganizations(data.organizations);

      SetAllEvents(data.allDriversCount);
      SetUpcomingEvents(data.activeDriversCount);
      SetThisMonthEvents(data.pendingDriversCount);
      SetCompletedEvents(data.declineDriversCount);


    };

    fetchRolesAndOrganizations();
  }, []);


  const sortedDrivers = useMemo(() => {
    return [...drivers].sort((a, b) => {
      const nameA = `${a.app_users?.first_name || ''} ${a.app_users?.last_name || ''}`.trim().toLowerCase();
      const nameB = `${b.app_users?.first_name || ''} ${b.app_users?.last_name || ''}`.trim().toLowerCase();
      if (sortOrder === 'asc') return nameA.localeCompare(nameB);
      if (sortOrder === 'desc') return nameB.localeCompare(nameA);
      return new Date(b.created_at || 0) - new Date(a.created_at || 0);
    });
  }, [drivers, sortOrder]);

  const handleImageClick = (src) => {
    setSelectedImage(src);
    setOpenImage(true);
  };

  const handleClose = () => {
    setOpenImage(false);
    setSelectedImage(null);
  };

  const handleStatusChange = async (id, status) => {
    const token = localStorage.getItem('token');
    const response = await fetch(`${API_URL}update-driver-status`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ status: status, id: id })
    });
    const data = await response.json();

    if (!response.ok || data.success === false) {
      // 🔑 Handle invalid token
      if (data.message === 'Invalid token') {
        localStorage.removeItem('token');
        window.location.href = '/login'; // redirect to login
        return;
      }


    }
    if (response.ok) {
      setToast({ open: true, message: "Status updated successfully", severity: "success" });
      setTimeout(() => window.location.reload(), 1000);

    } else {
      console.error('Failed to update status:', data);
    }
  };



  return (
    <>
      {!hideHeader && (
        <Helmet>
          <title>Driver Management - GPN</title>
        </Helmet>
      )}
      <Box>
        {/* Header */}
        {!hideHeader && (
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 4 }}>
            <Box>
              <Typography variant="h5" fontWeight={700}>Driver Management</Typography>
              <Typography variant="body2" color="text.secondary">
                Manage and monitor all drivers in your organization
              </Typography>
            </Box>
          </Box>
        )}

                {/* Compact Unified Toolbar */}
        <Paper
          elevation={0}
          sx={{
            p: 1.5,
            mb: 2.5,
            borderRadius: 2.5,
            background: isLight ? "#FFFFFF" : "rgba(255,255,255,0.04)",
            border: isLight ? "1px solid #E2E8F0" : "1px solid rgba(255,255,255,0.08)",
            backdropFilter: "blur(20px)",
          }}
        >
          {/* Row 1: Title, Count, Search, Sort & Actions */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              flexWrap: "wrap",
              gap: 1.5,
              pb: 1.25,
              borderBottom: isLight ? "1px solid #F1F5F9" : "1px solid rgba(255,255,255,0.06)",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <Typography variant="subtitle1" fontWeight={700} sx={{ color: isLight ? "#0F172A" : "#FFFFFF" }}>
                Registered Drivers
              </Typography>
              <Chip
                label={`${AllEvents} Total`}
                size="small"
                sx={{
                  height: 22,
                  fontSize: "0.75rem",
                  fontWeight: 700,
                  bgcolor: isLight ? "rgba(13, 148, 136, 0.1)" : "rgba(76, 161, 175, 0.2)",
                  color: isLight ? "#0D9488" : "#64d2e2",
                }}
              />
            </Box>

            <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
              <TextField
                size="small"
                placeholder="Search driver..."
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setPage(1);
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ color: isLight ? "#64748B" : "rgba(255,255,255,0.6)", fontSize: 18 }} />
                    </InputAdornment>
                  ),
                }}
                sx={{ width: { xs: "100%", sm: 220 }, "& .MuiInputBase-input": { py: 0.6, fontSize: "0.85rem" } }}
              />

              <Select
                size="small"
                value={sortOrder}
                onChange={(e) => setSortOrder(e.target.value)}
                displayEmpty
                renderValue={(val) => `Sort: ${val === 'asc' ? 'A → Z' : val === 'desc' ? 'Z → A' : 'Newest'}`}
                sx={{ width: 130, "& .MuiSelect-select": { py: 0.6, fontSize: "0.85rem" } }}
              >
                <MenuItem value="asc">Name: A → Z (Alphabetical)</MenuItem>
                <MenuItem value="desc">Name: Z → A</MenuItem>
                <MenuItem value="newest">Date: Newest First</MenuItem>
              </Select>
            </Box>
          </Box>

          {/* Row 2: Status Triage Chips & Secondary Filters */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              flexWrap: "wrap",
              gap: 1.25,
              pt: 1.25,
            }}
          >
            {/* Status Chips */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, flexWrap: "wrap" }}>
              {[
                { key: "All Status", label: "All", count: AllEvents },
                { key: "Approve", label: "Active", count: UpcomingEvents },
                { key: "Pending", label: "Pending", count: ThisMonthEvents },
                { key: "Decline", label: "Declined", count: CompletedEvents },
              ].map((tab) => {
                const isActive = status === tab.key;
                return (
                  <Chip
                    key={tab.key}
                    label={`${tab.label} (${tab.count})`}
                    size="small"
                    onClick={() => {
                      setStatus(tab.key);
                      setPage(1);
                    }}
                    sx={{
                      cursor: "pointer",
                      fontWeight: isActive ? 700 : 500,
                      fontSize: "0.78rem",
                      height: 26,
                      bgcolor: isActive
                        ? (isLight ? "#0D9488" : "#4CA1AF")
                        : (isLight ? "#F1F5F9" : "rgba(255,255,255,0.06)"),
                      color: isActive ? "#FFFFFF" : (isLight ? "#475569" : "rgba(255,255,255,0.7)"),
                      "&:hover": {
                        bgcolor: isActive
                          ? (isLight ? "#0F766E" : "#3b8b98")
                          : (isLight ? "#E2E8F0" : "rgba(255,255,255,0.12)"),
                      },
                    }}
                  />
                );
              })}
            </Box>

            {/* Date Filters + Org */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
              {user?.role === 1 && (
                <Autocomplete
                  size="small"
                  value={organization}
                  onChange={(event, newValue) => {
                    setOrganization(newValue);
                    setPage(1);
                  }}
                  options={orgOptions}
                  getOptionLabel={(option) => option?.org_name || ""}
                  renderInput={(params) => <TextField {...params} placeholder="Organization" />}
                  sx={{ width: 160, "& .MuiInputBase-input": { py: "2px !important", fontSize: "0.8rem" } }}
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                />
              )}
            </Box>
          </Box>
        </Paper>

        {/* Table */}
        <Paper>


        {/* Desktop Table View */}
        <TableContainer sx={{ display: { xs: 'none', md: 'block' } }}>



            <Table>
              <TableHead>
                <TableRow>

                  <TableCell>
                    <TableSortLabel
                      active={true}
                      direction={sortOrder}
                      onClick={() => setSortOrder(prev => prev === 'asc' ? 'desc' : 'asc')}
                    >
                      User
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>Vehicle Type</TableCell>
                  <TableCell>Vehicle Number</TableCell>
                  <TableCell>Licence</TableCell>
                  {user?.role === 1 && (
                    <TableCell>ORGANIZATION</TableCell>
                  )}
                  <TableCell>STATUS</TableCell>
                  <TableCell>ACTIONS</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>

                {loading && (
                  <div>
                    {/* <button onClick={fetchData}>Load Data</button> */}
                    <SectionLoader open={loading} />
                  </div>
                )}

                {sortedDrivers.length > 0 ? (
                  sortedDrivers.map((driver) => (
                    <TableRow
                      key={driver.id}
                      hover
                      onClick={() => {
                        setSelectedDriverForView(driver);
                        setDriverDetailModalOpen(true);
                      }}
                      sx={{
                        cursor: 'pointer',
                        transition: 'background-color 0.15s ease',
                        '&:hover': {
                          bgcolor: isLight ? '#F0FDFA !important' : 'rgba(13, 148, 136, 0.1) !important',
                        },
                        '& td': {
                          borderBottom: isLight ? '1px solid #E2E8F0' : '1px solid rgba(255,255,255,0.06)',
                          py: 1.5,
                          color: isLight ? '#1E293B' : '#F1F5F9',
                        }
                      }}
                    >

                      <TableCell>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            {driver.app_users.profile_pic ? (
                              <Avatar
                                src={driver.profile_pic}
                                alt={driver.app_users.first_name + ' ' + driver.app_users.last_name}
                                sx={{
                                  width: 40,
                                  height: 40,
                                  borderRadius: 2,
                                  bgcolor: "#f3f4f6",
                                  color: "#2C3E50",
                                  fontWeight: 700,
                                  fontSize: 20,
                                }}
                                imgProps={{
                                  onError: (e) => {
                                    e.target.onerror = null;
                                    e.target.src = "";
                                  },
                                }}
                              >
                                {driver.app_users.first_name?.[0]?.toUpperCase() || "?"}
                              </Avatar>
                            ) : (
                              <Avatar
                                sx={{
                                  width: 40,
                                  height: 40,
                                  borderRadius: 2,
                                  bgcolor: "#f3f4f6",
                                  color: "#2C3E50",
                                  fontWeight: 700,
                                  fontSize: 20,
                                }}
                              >
                                {driver.app_users.first_name?.[0]?.toUpperCase() || "?"}
                              </Avatar>
                            )}
                          </Box>
                          <Box>
                            <Typography fontWeight={600}>
                              {driver.app_users.first_name + ' ' + driver.app_users.last_name}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {driver.app_users.email}
                            </Typography>

                          </Box>
                        </Box>
                      </TableCell>

                      <TableCell>{driver.vehicle_type}</TableCell>
                      <TableCell>{driver.vehicle_number}</TableCell>
                      <TableCell>
                        <img
                          src={driver.license}
                          alt="Preview"
                          style={{ maxWidth: 40, maxHeight: 40, borderRadius: 4, cursor: 'pointer' }}
                          onClick={() => handleImageClick(driver.license)}
                        />
                      </TableCell>

                      {user?.role === 1 && (
                        <TableCell>{driver.origanisation.org_name}</TableCell>
                      )}

                      <TableCell>
                        <Chip
                          label={
                            driver.approve_status == 'Approve'
                              ? "Approved"
                              : driver.approve_status == 'Pending'
                                ? "Pending"
                                : "Decline"
                          }
                          color={statusColors[driver.approve_status]}
                          size="small"
                          sx={{ fontWeight: 600 }}
                        />


                      </TableCell>
                      <TableCell onClick={(e) => e.stopPropagation()}>
                        {driver.approve_status === "Pending" && (
                          <>
                            <IconButton
                              title="Approve"
                              onClick={() => handleStatusChange(driver.id, "Approve")}
                              sx={{ color: '#10B981', '&:hover': { background: 'rgba(16, 185, 129, 0.1)' } }}
                            >
                              <CheckCircleIcon fontSize="small" />
                            </IconButton>
                            <IconButton
                              title="Decline"
                              onClick={() => handleStatusChange(driver.id, "Decline")}
                              sx={{ color: '#EF4444', '&:hover': { background: 'rgba(239, 68, 68, 0.1)' } }}
                            >
                              <CancelIcon fontSize="small" />
                            </IconButton>
                          </>
                        )}
                        {driver.approve_status === "Approve" && (
                          <IconButton
                            title="Decline"
                            onClick={() => handleStatusChange(driver.id, "Decline")}
                            sx={{ color: '#EF4444', '&:hover': { background: 'rgba(239, 68, 68, 0.1)' } }}
                          >
                            <CancelIcon fontSize="small" />
                          </IconButton>
                        )}
                        {driver.approve_status === "Decline" && (
                          <IconButton
                            title="Approve"
                            onClick={() => handleStatusChange(driver.id, "Approve")}
                            sx={{ color: '#10B981', '&:hover': { background: 'rgba(16, 185, 129, 0.1)' } }}
                          >
                            <CheckCircleIcon fontSize="small" />
                          </IconButton>
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={7} sx={{ textAlign: "center" }}>
                      No Drivers found
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>

            </Table>
          </TableContainer>

          {/* Mobile Card View */}
          <Box sx={{ display: { xs: 'block', md: 'none' }, p: 1.5 }}>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
              {drivers.length > 0 ? (
                drivers.map((d) => (
                  <Card
                    key={d.id}
                    variant="outlined"
                    onClick={() => {
                      setSelectedDriverForView(d);
                      setDriverDetailModalOpen(true);
                    }}
                    sx={{ 
                      borderRadius: 2, 
                      background: 'rgba(255,255,255,0.03)',
                      borderColor: 'rgba(255,255,255,0.1)',
                      cursor: 'pointer',
                      transition: 'transform 0.15s ease',
                      '&:hover': { transform: 'translateY(-2px)' }
                    }}
                  >
                    <CardContent sx={{ p: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                        <Avatar
                          src={d.app_users.profile_pic}
                          sx={{ width: 48, height: 48, borderRadius: 2 }}
                        >
                          {d.app_users.first_name?.[0]?.toUpperCase() || "?"}
                        </Avatar>
                        <Box>
                          <Typography fontWeight={700} color="#FFFFFF">{d.app_users.first_name} {d.app_users.last_name}</Typography>
                          <Typography variant="caption" color="rgba(255,255,255,0.6)">{d.app_users.email}</Typography>
                        </Box>
                      </Box>
                      
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                        <Box>
                          <Typography variant="caption" color="rgba(255,255,255,0.5)" display="block">Vehicle</Typography>
                          <Typography variant="body2" color="#FFFFFF" fontWeight={600}>{d.vehicle_type} - {d.vehicle_number}</Typography>
                        </Box>
                        <Box sx={{ textAlign: 'right' }}>
                          <Typography variant="caption" color="rgba(255,255,255,0.5)" display="block">Status</Typography>
                          <Chip 
                            label={d.approve_status} 
                            color={statusColors[d.approve_status]} 
                            size="small" 
                            sx={{ height: 20, fontSize: '0.65rem' }} 
                          />
                        </Box>
                      </Box>

                      <Divider sx={{ my: 1.5, borderColor: 'rgba(255,255,255,0.05)' }} />
                      
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Box>
                          <Typography variant="caption" color="rgba(255,255,255,0.4)" display="block">Organization</Typography>
                          <Typography variant="body2" color="rgba(255,255,255,0.7)">{d.origanisation.org_name}</Typography>
                        </Box>
                        <Box>
                          {d.approve_status === "Pending" && (
                            <>
                              <IconButton size="small" sx={{ color: '#10B981' }} onClick={() => handleStatusChange(d.id, "Approve")}>
                                <CheckCircleIcon fontSize="small" />
                              </IconButton>
                              <IconButton size="small" sx={{ color: '#EF4444' }} onClick={() => handleStatusChange(d.id, "Decline")}>
                                <CancelIcon fontSize="small" />
                              </IconButton>
                            </>
                          )}
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                ))
              ) : (
                <Typography align="center" sx={{ py: 4, color: 'rgba(255,255,255,0.5)' }}>No drivers found</Typography>
              )}
            </Box>
          </Box>
          {/* Pagination */}
          {totalItems > 0 && (
            <>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", p: 2 }}>
                <Typography variant="body2">
                  Showing {page * pageSize - pageSize + 1}-{Math.min(page * pageSize, totalItems)} of {totalItems}
                </Typography>
                <Box>
                  <Button variant="outlined" sx={{ minWidth: 40, mx: 1 }} onClick={() => setPage(page - 1)} disabled={page === 1}>{'<'}</Button>
                  <Button variant="contained" sx={{ minWidth: 40, mx: 1, background: '#177373' }} onClick={() => setPage(page)}>{page}</Button>
                  <Button variant="outlined" sx={{ minWidth: 40, mx: 1 }} onClick={() => setPage(page + 1)} disabled={page === totalPages}>{'>'}</Button>
                </Box>
              </Box>

            </>

          )}
        </Paper>


        {/* Image Preview */}

        <Dialog open={openImage} onClose={handleClose} maxWidth="md">
          <DialogContent sx={{ position: 'relative', p: 0 }}>
            <IconButton
              onClick={handleClose}
              sx={{ position: 'absolute', top: 8, right: 8, color: '#fff', zIndex: 1 }}
            >
              <CloseIcon />
            </IconButton>
            <img
              src={selectedImage}
              alt="Enlarged"
              style={{ width: '100%', height: 'auto', display: 'block' }}
            />
          </DialogContent>
        </Dialog>

        {/* Driver Detail Modal Popup */}
        <Dialog
          open={driverDetailModalOpen}
          onClose={() => {
            setDriverDetailModalOpen(false);
            setSelectedDriverForView(null);
          }}
          fullWidth
          maxWidth="sm"
          PaperProps={{
            sx: {
              borderRadius: 3,
              bgcolor: isLight ? '#FFFFFF' : '#1e293b',
              backgroundImage: 'none',
              overflow: 'hidden',
            }
          }}
        >
          {selectedDriverForView && (
            <>
              <DialogTitle
                sx={{
                  p: 2.5,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  borderBottom: isLight ? '1px solid #E2E8F0' : '1px solid rgba(255,255,255,0.08)',
                }}
              >
                <Typography variant="h6" fontWeight={800} sx={{ color: isLight ? '#0F172A' : '#FFFFFF' }}>
                  Driver Profile & Verification
                </Typography>
                <Box display="flex" gap={1} alignItems="center">
                  <Chip
                    label={
                      selectedDriverForView.approve_status === 'Approve'
                        ? "Approved"
                        : selectedDriverForView.approve_status === 'Pending'
                          ? "Pending"
                          : "Declined"
                    }
                    color={statusColors[selectedDriverForView.approve_status] || "default"}
                    size="small"
                    sx={{ fontWeight: 700 }}
                  />
                  <IconButton
                    onClick={() => {
                      setDriverDetailModalOpen(false);
                      setSelectedDriverForView(null);
                    }}
                    size="small"
                  >
                    <CloseIcon />
                  </IconButton>
                </Box>
              </DialogTitle>

              <DialogContent sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                {/* Driver Info Card */}
                <Paper
                  variant="outlined"
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    bgcolor: isLight ? '#F8FAFC' : 'rgba(255,255,255,0.02)',
                    borderColor: isLight ? '#E2E8F0' : 'rgba(255,255,255,0.08)',
                  }}
                >
                  <Box display="flex" alignItems="center" gap={2}>
                    <Avatar
                      src={selectedDriverForView.app_users?.profile_pic || selectedDriverForView.profile_pic}
                      sx={{
                        width: 56,
                        height: 56,
                        bgcolor: '#0D9488',
                        color: '#FFF',
                        fontWeight: 700,
                        fontSize: 22
                      }}
                    >
                      {selectedDriverForView.app_users?.first_name?.[0]?.toUpperCase() || 'D'}
                    </Avatar>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="subtitle1" fontWeight={700} sx={{ color: isLight ? '#0F172A' : '#FFFFFF' }}>
                        {selectedDriverForView.app_users?.first_name} {selectedDriverForView.app_users?.last_name}
                      </Typography>
                      {selectedDriverForView.app_users?.email && (
                        <Typography variant="body2" color="text.secondary" noWrap>
                          ✉️ {selectedDriverForView.app_users.email}
                        </Typography>
                      )}
                      {selectedDriverForView.app_users?.phone_number && (
                        <Typography variant="body2" color="text.secondary">
                          📞 {selectedDriverForView.app_users.phone_number}
                        </Typography>
                      )}
                      {selectedDriverForView.origanisation?.org_name && (
                        <Typography variant="caption" sx={{ color: '#0D9488', fontWeight: 600, display: 'block', mt: 0.5 }}>
                          🏛️ {selectedDriverForView.origanisation.org_name}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                </Paper>

                {/* Vehicle Details */}
                <Paper
                  variant="outlined"
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    bgcolor: isLight ? '#F8FAFC' : 'rgba(255,255,255,0.02)',
                    borderColor: isLight ? '#E2E8F0' : 'rgba(255,255,255,0.08)',
                  }}
                >
                  <Typography variant="caption" fontWeight={800} sx={{ color: isLight ? '#64748B' : '#94A3B8', textTransform: 'uppercase', letterSpacing: 0.5, display: 'block', mb: 1.5 }}>
                    Vehicle Information
                  </Typography>
                  <Box display="grid" gridTemplateColumns="1fr 1fr" gap={1.5}>
                    <Box>
                      <Typography variant="caption" color="text.secondary" display="block">Vehicle Type</Typography>
                      <Typography variant="body2" fontWeight={700} sx={{ color: isLight ? '#0F172A' : '#FFFFFF' }}>
                        {selectedDriverForView.vehicle_type || 'N/A'}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary" display="block">License Plate / Number</Typography>
                      <Typography variant="body2" fontWeight={700} sx={{ color: isLight ? '#0F172A' : '#FFFFFF' }}>
                        {selectedDriverForView.vehicle_number || 'N/A'}
                      </Typography>
                    </Box>
                    {selectedDriverForView.vehicle_model && (
                      <Box>
                        <Typography variant="caption" color="text.secondary" display="block">Model / Make</Typography>
                        <Typography variant="body2" fontWeight={600}>{selectedDriverForView.vehicle_model}</Typography>
                      </Box>
                    )}
                    {selectedDriverForView.vehicle_color && (
                      <Box>
                        <Typography variant="caption" color="text.secondary" display="block">Color</Typography>
                        <Typography variant="body2" fontWeight={600}>{selectedDriverForView.vehicle_color}</Typography>
                      </Box>
                    )}
                  </Box>
                </Paper>

                {/* Driving License Photo */}
                {selectedDriverForView.license && (
                  <Paper
                    variant="outlined"
                    sx={{
                      p: 2,
                      borderRadius: 2,
                      bgcolor: isLight ? '#F8FAFC' : 'rgba(255,255,255,0.02)',
                      borderColor: isLight ? '#E2E8F0' : 'rgba(255,255,255,0.08)',
                    }}
                  >
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                      <Typography variant="caption" fontWeight={800} sx={{ color: isLight ? '#64748B' : '#94A3B8', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                        Driver License Document
                      </Typography>
                      <Typography variant="caption" color="primary" sx={{ cursor: 'pointer', fontWeight: 600 }} onClick={() => handleImageClick(selectedDriverForView.license)}>
                        View Fullscreen
                      </Typography>
                    </Box>
                    <Box
                      onClick={() => handleImageClick(selectedDriverForView.license)}
                      sx={{
                        borderRadius: 2,
                        overflow: 'hidden',
                        cursor: 'pointer',
                        maxHeight: 240,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        bgcolor: '#000',
                        border: '1px solid rgba(0,0,0,0.1)'
                      }}
                    >
                      <img
                        src={selectedDriverForView.license}
                        alt="License"
                        style={{ maxWidth: '100%', maxHeight: 240, objectFit: 'contain' }}
                      />
                    </Box>
                  </Paper>
                )}
              </DialogContent>

              <DialogActions sx={{ p: 2, px: 3, borderTop: isLight ? '1px solid #E2E8F0' : '1px solid rgba(255,255,255,0.08)', gap: 1 }}>
                {selectedDriverForView.approve_status !== 'Approve' && (
                  <Button
                    onClick={() => {
                      handleStatusChange(selectedDriverForView.id, "Approve");
                      setDriverDetailModalOpen(false);
                    }}
                    variant="contained"
                    color="success"
                    size="small"
                    startIcon={<CheckCircleIcon />}
                  >
                    Approve Driver
                  </Button>
                )}
                {selectedDriverForView.approve_status !== 'Decline' && (
                  <Button
                    onClick={() => {
                      handleStatusChange(selectedDriverForView.id, "Decline");
                      setDriverDetailModalOpen(false);
                    }}
                    variant="contained"
                    color="error"
                    size="small"
                    startIcon={<CancelIcon />}
                  >
                    Decline Driver
                  </Button>
                )}
                <Button
                  onClick={() => {
                    setDriverDetailModalOpen(false);
                    setSelectedDriverForView(null);
                  }}
                  variant="outlined"
                  color="inherit"
                  size="small"
                >
                  Close
                </Button>
              </DialogActions>
            </>
          )}
        </Dialog>

        <Toast toast={toast} setToast={setToast} />

        <ModuleSettingsModal
          open={settingsOpen}
          onClose={() => setSettingsOpen(false)}
          moduleKey="rider"
          moduleTitle="Drivers / Riders"
        />
      </Box>
    </>
  );
};

export default Drivers;